import React, { useEffect } from 'react';
import { getIntl } from '../../../utils/intl';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import useProfileConnectedHealthSystems from '../../../hooks/api/useProfileConnectedHealthSystems';
import EndpointCard from '../EndpointCard/EndpointCard';
import getDateString from '../../../utils/getDateString';
import getName from '../../../utils/getName';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import useOneUpHealthRefresh from '../../../hooks/api/useOneUpRefresh';
import MedicalProfileUsageIndicator from '../MedicalProfileUsageIndicator';

import { useIsProfilePaid } from '../../../hooks/api/useIsProfilePaid';
import FastenConnectionsList from './FastenConnectionsList';
import Divider from '../../v2BaseComponents/Divider';

const intl = getIntl('profile-connect-tab', {
    addPortal: 'Add Patient Portal',
    connected: 'Connected: {date}',
    patientPortals: 'Patient Portals',
    noAccessAddPortalButtonMessage:
        'You have View Only access to the connections. Contact the profile organizer to edit your permission if needed.',
    noData: 'Data has not yet been loaded for health system',
    refreshDate: 'Last Refreshed: {date}',
});

export type PortalListOwnProps = {
    medicalProfileId: string;
    isAForceRefresh?: boolean;
};

export type PortalListProps = PortalListOwnProps;

const PortalList: React.FC<PortalListProps> = (props) => {
    const navigation = useProfilesTabNavigation();
    const { isLoading, connectedHealthSystems, pendingDataHealthSystems } = useProfileConnectedHealthSystems({
        medicalProfileId: props.medicalProfileId,
    });
    const { isPaid } = useIsProfilePaid({
        profileId: props.medicalProfileId,
    });
    const { refresh } = useOneUpHealthRefresh({
        suppressSuccessNotification: true,
    });
    useEffect(() => {
        if (props.isAForceRefresh) {
            refresh(props.medicalProfileId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.isAForceRefresh, props.medicalProfileId]);

    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <Stack vertical fullWidth space={contentStackSpacing}>
                    {connectedHealthSystems?.length || pendingDataHealthSystems.length ? (
                        <>
                            <Stack horizontal fullWidth space={contentStackSpacing} justify="space-between">
                                <Text variant="h4">{intl('patientPortals')}</Text>
                                <MedicalProfileUsageIndicator
                                    medicalProfileId={props.medicalProfileId}
                                    metricType="portal-connect"
                                />
                            </Stack>
                            {pendingDataHealthSystems.map((healthSystem) => {
                                const dateString = getDateString(healthSystem.connectedTime, 'date-time');
                                return (
                                    <EndpointCard
                                        key={healthSystem.endpoint.id}
                                        endpoint={healthSystem.endpoint}
                                        mutedSubText={intl('connected', { date: dateString })}
                                        omitAddress
                                        subText={intl('noData')}
                                        onPress={() =>
                                            navigation.navigate('portal-connection', {
                                                profileId: props.medicalProfileId,
                                                healthSystemId: String(healthSystem.endpoint.id),
                                            })
                                        }
                                        automaticRefreshExpirationDateTime={
                                            healthSystem.automaticRefreshExpirationDateTime
                                        }
                                        isFreeUser={!isPaid}
                                    />
                                );
                            })}
                            {connectedHealthSystems.map((healthSystem) => {
                                const dateString = getDateString(healthSystem.connectedTime, 'date-time');
                                return (
                                    <EndpointCard
                                        key={healthSystem.endpoint.id}
                                        endpoint={healthSystem.endpoint}
                                        omitAddress
                                        portalPatientName={getName({
                                            firstName: healthSystem.patientRecordName?.first,
                                            middleName: healthSystem.patientRecordName?.middle,
                                            lastName: healthSystem.patientRecordName?.last,
                                        })}
                                        mutedSubText={intl('connected', { date: dateString })}
                                        subText={
                                            healthSystem.refreshedTime
                                                ? intl('refreshDate', {
                                                      date: getDateString(healthSystem.refreshedTime, 'date-time'),
                                                  })
                                                : undefined
                                        }
                                        onPress={() =>
                                            navigation.navigate('portal-connection', {
                                                profileId: props.medicalProfileId,
                                                healthSystemId: String(healthSystem.endpoint.id),
                                            })
                                        }
                                        automaticRefreshExpirationDateTime={
                                            healthSystem.automaticRefreshExpirationDateTime
                                        }
                                        userDisabledAutomaticRefresh={healthSystem.userDisabledAutomaticRefresh}
                                        isFreeUser={!isPaid}
                                    />
                                );
                            })}
                        </>
                    ) : null}
                    {connectedHealthSystems?.length || pendingDataHealthSystems.length ? (
                        <Divider horizontal style={{ marginVertical: 16 }} />
                    ) : null}
                    <FastenConnectionsList medicalProfileId={props.medicalProfileId} />
                </Stack>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(PortalList);
